/**
 * Per-site Pages management (`/settings/pages`).
 *
 * The inventory surface for the URLs tracked under the active site —
 * add / rename / enable-or-pause / delete. Complements the sibling
 * `/settings/sites` ("Audits") tab, which owns the schedule + coverage
 * grid that decides how these pages get audited.
 *
 * Site is read from `useActiveSiteUuid()` (the chrome's active-site
 * context), not the URL — switch sites via the settings breadcrumb's
 * site pill, same as the Audits tab.
 *
 * The settings layout (`_app/settings.tsx`) owns the <Island> + nav,
 * so this route renders its body straight into the outlet.
 *
 * Nothing here is new plumbing: `useSitePages` lists, `AddPageDialog`
 * (`useCreatePage`) adds, `EditPageDialog` (`useUpdatePage`) edits the
 * name + URL, the per-row Switch flips `enabled` via `useUpdatePage`,
 * and `useDeletePage` removes. Delete is admin-only to mirror the API's
 * `requireAdminLike` guard on `DELETE /v1/pages/:uuid`.
 */
import { AddPageDialog } from '@/components/pages/AddPageDialog';
import { EditPageDialog } from '@/components/pages/EditPageDialog';
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { ErrorState } from '@/components/primitives/ErrorState';
import { Button } from '@/components/ui/button';
import { Menu, MenuItem, MenuPopup, MenuTrigger } from '@/components/ui/menu';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useActiveSiteUuid } from '@/lib/active-site';
import type { Page } from '@/lib/api-types';
import { toastFromError, toastSuccess } from '@/lib/errors';
import { truncate } from '@/lib/format';
import { useDeletePage, useSite, useSitePages, useUpdatePage } from '@/lib/queries';
import { useOrganization } from '@clerk/react';
import { Browsers, DotsThreeVertical, PencilSimple, Plus, Trash } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/pages')({
  component: PagesSettingsPage,
});

function PagesSettingsPage(): ReactElement {
  const siteUuid = useActiveSiteUuid();
  const site = useSite(siteUuid);
  const pages = useSitePages(siteUuid);

  // Delete is admin-gated to mirror the API's `requireAdminLike` guard.
  // Rename + enable/pause only need `sites:write`, which members have —
  // so those stay available to everyone who can see this page.
  const { membership } = useOrganization();
  const isAdmin = membership?.role === 'admin' || membership?.role === 'org:admin';

  // Add/rename open state + the row the destructive/rename dialogs act
  // on. Lifting the target up here keeps a single dialog instance for
  // the whole table rather than one per row.
  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Page | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Page | null>(null);
  const deletePage = useDeletePage();

  async function onDeleteConfirm(): Promise<void> {
    if (!deleteTarget) return;
    try {
      await deletePage.mutateAsync({ uuid: deleteTarget.uuid, siteUuid: deleteTarget.site_uuid });
      toastSuccess('Page deleted', deleteTarget.name);
      setDeleteTarget(null);
    } catch (err) {
      toastFromError(err);
    }
  }

  // No active site → nothing to manage. Shouldn't happen in practice
  // (the breadcrumb's site pill always has a selection) but a deep link
  // could hit it. Mirrors the Audits tab's soft prompt.
  if (!siteUuid) {
    return (
      <ErrorState
        title="No site selected"
        error={new Error('Pick a site from the switcher to manage its pages.')}
        retry={undefined}
      />
    );
  }

  const rows = pages.data ?? [];

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Pages</h2>
          <p className="text-sm text-[var(--color-mute)]">
            The URLs audited under this site. How often each page is audited — and on which devices
            and regions — is set on the{' '}
            <Link
              to="/settings/sites"
              className="text-[var(--color-ink)] underline-offset-2 hover:underline"
            >
              Audits
            </Link>{' '}
            tab.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus aria-hidden="true" />
          Add page
        </Button>
      </header>

      {pages.isError && (
        <ErrorState
          title="Couldn't load pages"
          error={pages.error}
          retry={() => {
            void pages.refetch();
          }}
        />
      )}

      {pages.isLoading && (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      )}

      {!pages.isLoading && !pages.isError && rows.length === 0 && (
        <EmptyState
          icon={Browsers}
          title="No pages tracked yet"
          description="Add a page to track audits against a specific URL on this site."
          action={
            <Button variant="outline" onClick={() => setAddOpen(true)}>
              <Plus aria-hidden="true" />
              Add page
            </Button>
          }
        />
      )}

      {!pages.isLoading && !pages.isError && rows.length > 0 && (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-[var(--color-line-soft)] text-left text-xs uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  <th className="px-4 py-2 font-medium">Page</th>
                  <th className="w-28 px-4 py-2 font-medium">Enabled</th>
                  <th className="w-16 px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <PageRow
                    key={p.uuid}
                    page={p}
                    isAdmin={isAdmin}
                    onEdit={() => setEditTarget(p)}
                    onDelete={() => setDeleteTarget(p)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <AddPageDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        siteUuid={siteUuid}
        siteOrigin={site.data?.url}
      />
      <EditPageDialog
        open={editTarget !== null}
        onOpenChange={(next) => {
          if (!next) setEditTarget(null);
        }}
        page={editTarget}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(next) => {
          if (!next) setDeleteTarget(null);
        }}
        title="Delete this page?"
        description={
          deleteTarget
            ? `"${deleteTarget.name}" will stop being audited and drop out of the audits list. Its audit history is kept for 7 days, then permanently removed.`
            : undefined
        }
        confirmLabel="Delete page"
        destructive
        loading={deletePage.isPending}
        onConfirm={onDeleteConfirm}
      />
    </div>
  );
}

function PageRow({
  page,
  isAdmin,
  onEdit,
  onDelete,
}: {
  page: Page;
  isAdmin: boolean;
  onEdit: () => void;
  onDelete: () => void;
}): ReactElement {
  // Per-row instance so `isPending` disables only this row's Switch
  // while its PATCH is in flight — mirrors MemberRow's local `updating`.
  const updatePage = useUpdatePage();

  async function onToggleEnabled(next: boolean): Promise<void> {
    try {
      await updatePage.mutateAsync({
        uuid: page.uuid,
        siteUuid: page.site_uuid,
        body: { enabled: next },
      });
      toastSuccess(next ? 'Page enabled' : 'Page paused', page.name);
    } catch (err) {
      toastFromError(err);
    }
  }

  return (
    <tr className="border-b border-[var(--color-line-soft)]/70">
      <td className="px-4 py-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-[var(--color-ink)]">{page.name}</div>
          <div className="truncate font-mono text-xs text-[var(--color-mute)]">
            {truncate(page.url, 80)}
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        <Switch
          checked={page.enabled}
          disabled={updatePage.isPending}
          onCheckedChange={(next) => {
            void onToggleEnabled(next);
          }}
          aria-label={
            page.enabled ? `Pause audits for ${page.name}` : `Enable audits for ${page.name}`
          }
        />
      </td>
      <td className="px-4 py-3 text-right">
        <Menu>
          <MenuTrigger
            render={<Button size="icon" variant="ghost" aria-label={`Actions for ${page.name}`} />}
          >
            <DotsThreeVertical aria-hidden="true" />
          </MenuTrigger>
          <MenuPopup align="end">
            <MenuItem onClick={onEdit}>
              <PencilSimple aria-hidden="true" />
              Edit…
            </MenuItem>
            {isAdmin && (
              <MenuItem onClick={onDelete}>
                <Trash aria-hidden="true" />
                Delete…
              </MenuItem>
            )}
          </MenuPopup>
        </Menu>
      </td>
    </tr>
  );
}
